'use client'

import React, { useState } from 'react'
import { usePathname } from 'next/navigation'
import { ChatPanel } from './ChatPanel'
import { ChatMascot } from './ChatMascot'
import classes from './index.module.css'
import { useCartModalOpen, CART_MODAL_OFFSET_PX } from '@/components/Cart/useCartModalOpen'

/**
 * Static chat button — a clean, fixed-position button in the bottom-right
 * corner that opens the chat panel when clicked.
 *
 * Replaces the previous animated sprite mascot. Uses the same ChatPanel
 * component for consistency.
 */
export const ChatButton: React.FC = () => {
  // On phones the floating button must never cover the Checkout / place-order
  // button, so it is hidden on the cart and checkout pages below md.
  const pathname = usePathname()
  const hideOnMobile = /^\/(?:en|de)\/(?:cart|checkout)(?:\/|$)/.test(pathname ?? '')
  const [isOpen, setIsOpen] = useState(false)
  // Drive the mascot shift in lock-step with the cart Sheet. The
  // button itself uses a CSS class (body.cart-modal-open → right:
  // shifted); we pass an explicit `right` to the mascot so it
  // animates via its own `transition: right` (NO wrapper — see
  // ChatMascot.tsx comment about the stacking-context trap).
  const cartModalOpen = useCartModalOpen()
  // 40px (original perched position from the right edge) + 488px
  // (the Sheet's width + breathing gap, kept in lock-step with
  // CART_MODAL_OFFSET_PX so the mascot lands at the same x as the
  // chat button + 16px gap when the cart is open).
  const mascotRightPx = 40 + (cartModalOpen ? CART_MODAL_OFFSET_PX : 0)
  // Perch on the button, which sits at bottom:112px to clear the Google
  // Merchant badge (index.module.css, SITE-AUDIT-2026-11-04 #1).
  const MASCOT_BOTTOM_PX = 138

  const handleClick = () => {
    setIsOpen((prev) => !prev)
  }

  return (
    <>
      <button
        onClick={handleClick}
        className={`${classes.chatButton} ${isOpen ? classes.open : ''} ${hideOnMobile ? classes.hideOnMobile : ''}`}
        aria-label={isOpen ? 'Chat schließen' : 'Chat öffnen'}
        aria-expanded={isOpen}
      >
        <svg
          className={classes.icon}
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          {isOpen ? (
            <>
              {/* X (close). Two diagonals: top-left→bottom-right AND
                  top-right→bottom-left. (Previously both lines were
                  identical — bug 2026-07-26, only one hand of the X
                  showed. Fixed.) */}
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </>
          ) : (
            <>
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
              <circle cx="9" cy="10" r="0.5" fill="currentColor" />
              <circle cx="12" cy="10" r="0.5" fill="currentColor" />
              <circle cx="15" cy="10" r="0.5" fill="currentColor" />
            </>
          )}
        </svg>
        {!isOpen && <span className={classes.label}>Chat</span>}
      </button>

      {/* Robot perched on top of the button while the chat is closed.
          Direct child of the fixed positioning — we do NOT wrap this
          in a motion.div / any element with a transform, because
          transforms create a new stacking context and would push the
          mascot's z-index into a local context (where the button at
          z-40 would paint over it). The mascot's z-100 + CSS
          `transition: right` does the slide cleanly. */}
      {!isOpen && (
        <ChatMascot
          className={classes.mascot}
          zIndex={100}
          style={{ right: `${mascotRightPx}px`, bottom: `${MASCOT_BOTTOM_PX}px` }}
          onClick={handleClick}
        />
      )}

      {isOpen && <ChatPanel isOpen={isOpen} onClose={() => setIsOpen(false)} />}
    </>
  )
}

export default ChatButton
